import { createApp } from 'vue'
import App from './App.vue'
import { PROVIDER_CONSTANTS } from './constants/provider.constants'
import router from './router'
import { UserService } from './services/user.service'
import Rollbar from 'rollbar'
import { CaliberService } from './services/caliber.service'
import { CartridgeService } from './services/cartridge.service'
import { SupplierService } from './services/supplier.service'
import Toaster from '@meforma/vue-toaster'
import { ContactService } from './services/contact.service'
import moment from 'moment'
import { ManufacturerService } from './services/manufacturer.service'
import { CustomerService } from './services/customer.service'
import { NoteService } from './services/note.service'
import { InventoryTypeService } from './services/inventoryType.service'
import { InventoryItemService } from './services/inventoryItem.service'
import { BuildTypeService } from './services/buildType.service'
import { InventoryTypeFieldService } from './services/inventoryTypeField.service'
import { ServiceService } from './services/service.service'
import { PurchaseOrderService } from './services/purchaseOrder.service'
import { PurchaseOrderItemService } from './services/purchaseOrderItem.service'
import { OrderService } from './services/order.service'
import { QuoteService } from './services/quote.service'
import { BuildStepService } from './services/buildStep.service'
import { DashboardInformationService } from './services/dashboardInformation.service'
import { ServiceFieldService } from './services/serviceField.service'
import { LOCAL_STORAGE_CONSTANTS } from './constants/localstorage.contstants'
import { MiscellaneousService } from './services/miscellaneous.service'
import { OrderBuildStepService } from './services/orderBuildStep.service'
import { OrderPartService } from './services/orderPart.service'
import { OrderServiceService } from './services/orderService.service'
import { QuotePartService } from './services/quotePart.service'
import { QuoteServiceService } from './services/quoteService.service'
import { CustomerOrderService } from './services/customerOrder.service'
import { CustomerQuoteService } from './services/customerQuote.service'
import { RoleService } from './services/role.service'
import { StateService } from './services/states.service'

const app = createApp(App)

app.use(Toaster, {
  position: 'top-right'
})

if (process.env.NODE_ENV === 'staging' || process.env.NODE_ENV === 'production') {
  const logger = new Rollbar({
    accessToken: '86cb5bb6d0c7486793481408d32a9cb9',
    captureUncaught: true,
    captureUnhandledRejections: true,
    environment: process.env.NODE_ENV
  })

  app.config.errorHandler = function(error): void {
    const log = error as Error
    logger.error(log)
  }
}

const miscellaneousService = new MiscellaneousService()

const buildStepService = new BuildStepService(miscellaneousService)
const buildTypeService = new BuildTypeService(miscellaneousService)
const caliberService = new CaliberService(miscellaneousService)
const cartridgeService = new CartridgeService(miscellaneousService)
const contactService = new ContactService(miscellaneousService)
const customerOrderService = new CustomerOrderService(miscellaneousService)
const customerQuoteService = new CustomerQuoteService(miscellaneousService)
const customerService = new CustomerService(miscellaneousService)
const dashboardInformationService = new DashboardInformationService(miscellaneousService)
const inventoryItemService = new InventoryItemService(miscellaneousService)
const inventoryTypeFieldService = new InventoryTypeFieldService(miscellaneousService)
const inventoryTypeService = new InventoryTypeService(miscellaneousService)
const manufacturerService = new ManufacturerService(miscellaneousService)
const noteService = new NoteService(miscellaneousService)
const orderBuildStepService = new OrderBuildStepService(miscellaneousService)
const orderPartService = new OrderPartService(miscellaneousService)
const orderService = new OrderService(miscellaneousService)
const orderServiceService = new OrderServiceService(miscellaneousService)
const purchaseOrderItemService = new PurchaseOrderItemService(miscellaneousService)
const purchaseOrderService = new PurchaseOrderService(miscellaneousService)
const quotePartService = new QuotePartService(miscellaneousService)
const quoteService = new QuoteService(miscellaneousService)
const quoteServiceService = new QuoteServiceService(miscellaneousService)
const roleService = new RoleService(miscellaneousService)
const serviceFieldService = new ServiceFieldService(miscellaneousService)
const serviceService = new ServiceService(miscellaneousService)
const stateService = new StateService(miscellaneousService)
const supplierService = new SupplierService(miscellaneousService)
const userService = new UserService(miscellaneousService)

app.provide(PROVIDER_CONSTANTS.buildStepService, buildStepService)
app.provide(PROVIDER_CONSTANTS.buildTypeService, buildTypeService)
app.provide(PROVIDER_CONSTANTS.caliberService, caliberService)
app.provide(PROVIDER_CONSTANTS.cartridgeService, cartridgeService)
app.provide(PROVIDER_CONSTANTS.contactService, contactService)
app.provide(PROVIDER_CONSTANTS.customerOrderService, customerOrderService)
app.provide(PROVIDER_CONSTANTS.customerQuoteService, customerQuoteService)
app.provide(PROVIDER_CONSTANTS.customerService, customerService)
app.provide(PROVIDER_CONSTANTS.dashboardInformationService, dashboardInformationService)
app.provide(PROVIDER_CONSTANTS.inventoryItemService, inventoryItemService)
app.provide(PROVIDER_CONSTANTS.inventoryTypeFieldService, inventoryTypeFieldService)
app.provide(PROVIDER_CONSTANTS.inventoryTypeService, inventoryTypeService)
app.provide(PROVIDER_CONSTANTS.manufacturerService, manufacturerService)
app.provide(PROVIDER_CONSTANTS.miscellaneousService, miscellaneousService)
app.provide(PROVIDER_CONSTANTS.noteService, noteService)
app.provide(PROVIDER_CONSTANTS.orderBuildStepService, orderBuildStepService)
app.provide(PROVIDER_CONSTANTS.orderPartService, orderPartService)
app.provide(PROVIDER_CONSTANTS.orderService, orderService)
app.provide(PROVIDER_CONSTANTS.orderServiceService, orderServiceService)
app.provide(PROVIDER_CONSTANTS.purchaseOrderItemService, purchaseOrderItemService)
app.provide(PROVIDER_CONSTANTS.purchaseOrderService, purchaseOrderService)
app.provide(PROVIDER_CONSTANTS.quotePartService, quotePartService)
app.provide(PROVIDER_CONSTANTS.quoteService, quoteService)
app.provide(PROVIDER_CONSTANTS.quoteServiceService, quoteServiceService)
app.provide(PROVIDER_CONSTANTS.roleService, roleService)
app.provide(PROVIDER_CONSTANTS.serviceFieldService, serviceFieldService)
app.provide(PROVIDER_CONSTANTS.serviceService, serviceService)
app.provide(PROVIDER_CONSTANTS.stateService, stateService)
app.provide(PROVIDER_CONSTANTS.supplierService, supplierService)
app.provide(PROVIDER_CONSTANTS.userService, userService)

app.provide(PROVIDER_CONSTANTS.toaster, app.config.globalProperties.$toast)

app.config.globalProperties.$methods = {
  formatDate(date: string|undefined): string {
    if (!date || date === undefined || date === '')
      return ''

    const momentDate = moment.utc(date)
    const localDate = momentDate.local()
    return localDate.format("MMM DD, YYYY")
  },
  formatPhoneNumber(phoneNumber: string): string {
    if (!phoneNumber) return ''

    const a = phoneNumber.replace(/\D/g, "").match(/(\d{0,3})(\d{0,3})(\d{0,4})/)
    if (a === null) return ''

    return !a[2] ? a[1] : "(" + a[1] + ") " + a[2] + (a[3] ? "-" + a[3] : "")
  },
  formatPrice(price: number): string {
    return '$ ' + price.toFixed(2).replace(/(\d)(?=(\d{3})+(?:\.\d+)?$)/g, '$1,')
  }
}

app.directive('click-outside', {
  beforeMount: function (el, binding) {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const ourClickEventHandler = (event: { target: unknown }) => {
      if (!el.contains(event.target) && el !== event.target)
        binding.value(event)
    }

    el.__vueClickEventHandler__ = ourClickEventHandler

    document.addEventListener('click', ourClickEventHandler)
  },
  unmounted: function (el) {
    document.removeEventListener('click', el.__vueClickEventHandler__)
  }
})

router.beforeEach((to, _from, next) => {
  const accessToken = localStorage.getItem(LOCAL_STORAGE_CONSTANTS.accessToken)
  const isAuthenticated = accessToken !== null && accessToken !== ''

  const isEmployeeFlag = localStorage.getItem(LOCAL_STORAGE_CONSTANTS.isEmployee)
  const isEmployee = isEmployeeFlag !== null && isEmployeeFlag === 'true'

  if (to.matched.some(x => !x.meta.requiresLogin || (isAuthenticated && (!x.meta.employeeOnly || isEmployee)))) {
    document.title = to.meta.title
    next()
  } else {
    next(`/?returnUrl=${encodeURIComponent(to.fullPath)}`)
  }
})

app.use(router).mount('#app')
